import React from 'react';
import PageHero from '@/components/PageHero';
import FadeInUp from '@/components/FadeInUp';
import TrustBadges from '@/components/TrustBadges';
import { ShieldIcon, HardHatIcon, UsersIcon, CertificateIcon, CheckIcon } from '@/components/icons';

const PILLARS = [
  {
    icon: ShieldIcon,
    label: 'Superior Service',
    title: 'A name in the sector for 25+ years.',
    text: 'National Forklift Training has been an important presence in the New Zealand forklift training sector for over twenty-five years. Our team is fully qualified and experienced training professionals — renowned for an authoritative, friendly, and competent approach. We give clients a choice of training locations and equipment to suit their operation.',
  },
  {
    icon: HardHatIcon,
    label: 'A Relevant History',
    title: 'Founded by people who lived it.',
    text: 'NFT was founded in 1990 by people deeply experienced operationally — they brought a working lifetime on the job and applied modern, compliance-related systems to deliver outstanding outcomes. We’ve pushed the industry forward with innovations like photo identification certification, the “Safety Awareness Workshop” and the “Destination Home” programme.',
  },
  {
    icon: UsersIcon,
    label: 'Customer Loyalty',
    title: 'A Who’s Who of NZ industry trusts us.',
    text: 'We’ve built long-term relationships with a Who’s Who of New Zealand industry. Customised training programmes give every client a choice of equipment, location, and session times. Our centrally located training centre in East Tamaki keeps everything close at hand.',
  },
];

const TIMELINE = [
  { year: '1990', title: 'NFT Founded', text: 'Established by experienced operators who saw the need for serious, compliance-focused training in NZ.' },
  { year: '1996', title: 'Auckland Training Centre', text: 'Permanent training facility opened in East Tamaki industrial heartland.' },
  { year: '2002', title: 'Photo ID Certification', text: 'Among the first in NZ to issue Photo ID licence cards — making WorkSafeNZ audits painless.' },
  { year: '2008', title: 'Safety Awareness Workshops', text: 'Launched signature workshop programme for industry-wide safety culture lift.' },
  { year: '2015', title: '“Destination Home” Programme', text: 'Industry-leading initiative emphasising operator wellbeing and safe daily return home.' },
  { year: 'Today', title: '4.9★ · 25+ Years', text: '112 verified Google reviews. Daily public courses + 24/7 on-site service nationwide.' },
];

const VALUES = ['Authority', 'Friendliness', 'Competence', 'Safety', 'NZ Owned'];

export default function About() {
  return (
    <>
      <PageHero
        eyebrow="About NFT"
        title="Three decades of forklift training, the Kiwi way."
        subtitle="Founded by operators, run by professionals. Audited by Competenz, approved by WorkSafeNZ — trusted by some of the biggest names in NZ industry."
        image="/generated/training-facility.png"
        breadcrumbs={[{label: 'Home', to: '/'}, {label: 'About'}]}
      />

      {/* PILLARS */}
      <section className="nft-container py-16 lg:py-24">
        <FadeInUp>
          <div className="max-w-3xl">
            <div className="label-eyebrow text-[#FC4E2C] mb-3">Why NFT</div>
            <h2 className="font-display font-extrabold uppercase text-4xl lg:text-5xl text-[#060D9C] leading-[0.95]">
              Three things we get right, every time.
            </h2>
            <span className="section-rule mt-5" />
          </div>
        </FadeInUp>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <FadeInUp key={i} delay={i * 0.08}>
                <div className="surface-card p-7 h-full">
                  <div className="w-12 h-12 rounded-xl bg-[#FC4E2C]/15 text-[#FC4E2C] flex items-center justify-center mb-5">
                    <Icon width={26} height={26} />
                  </div>
                  <div className="label-eyebrow text-[#FC4E2C] mb-2">{p.label}</div>
                  <h3 className="font-display font-extrabold uppercase text-2xl text-[#060D9C] leading-tight">{p.title}</h3>
                  <p className="mt-3 text-[#060D9C]/75 text-sm leading-relaxed">{p.text}</p>
                </div>
              </FadeInUp>
            );
          })}
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-[#060D9C] text-white relative" data-testid="about-timeline">
        <div className="nft-container py-16 lg:py-24">
          <FadeInUp>
            <div className="label-eyebrow text-[#FC4E2C] mb-3">Our Timeline</div>
            <h2 className="font-display font-extrabold uppercase text-4xl lg:text-5xl leading-[0.95]">
              From <span className="text-[#FC4E2C]">1990</span> to today.
            </h2>
          </FadeInUp>

          <div className="mt-12 relative">
            <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-0.5 bg-white/20" aria-hidden="true" />
            <div className="space-y-8 lg:space-y-16">
              {TIMELINE.map((t, i) => (
                <FadeInUp key={t.year} delay={i * 0.05}>
                  <div className={`relative flex items-start gap-6 lg:gap-12 ${i % 2 === 1 ? 'lg:flex-row-reverse lg:text-right' : ''}`}>
                    <div className="shrink-0 lg:w-1/2 flex lg:justify-end">
                      <div className="pl-12 lg:pl-0 lg:pr-12 lg:max-w-[420px] w-full">
                        <div className="font-mono-cert text-[#FC4E2C] text-xl mb-1">{t.year}</div>
                        <h3 className="font-display font-extrabold uppercase text-2xl mb-2">{t.title}</h3>
                        <p className="text-white/75 text-sm leading-relaxed">{t.text}</p>
                      </div>
                    </div>
                    <div className="absolute left-4 lg:left-1/2 -translate-x-1/2 top-1.5 w-4 h-4 rounded-full bg-[#FC4E2C] border-4 border-[#060D9C] shadow-lg" />
                    <div className="hidden lg:block lg:w-1/2" />
                  </div>
                </FadeInUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VALUES STRIP */}
      <section className="hi-vis-stripe py-1" aria-hidden="true" />
      <section className="bg-white py-12" data-testid="about-values-strip">
        <div className="nft-container">
          <div className="flex flex-wrap items-center justify-center gap-3 lg:gap-6">
            {VALUES.map((v, i) => (
              <React.Fragment key={v}>
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-[#2D8653]/15 text-[#2D8653] flex items-center justify-center">
                    <CheckIcon width={14} height={14} />
                  </span>
                  <span className="font-display font-extrabold uppercase text-2xl lg:text-3xl text-[#060D9C]">{v}</span>
                </div>
                {i < VALUES.length - 1 && <span className="text-[#FC4E2C] hidden sm:inline">/</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>
      <section className="hi-vis-stripe py-1" aria-hidden="true" />

      {/* TRUST BADGES */}
      <section className="nft-container py-16 lg:py-24">
        <FadeInUp>
          <TrustBadges />
        </FadeInUp>
      </section>
    </>
  );
}
